import './validityCategorySelector.scss';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IntlShape } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import { Chip, ChipGroup, Skeleton } from '@eliberty/design-system-react';

import type { DisplayMode } from '~atoms';
import { DISPLAY_MODE, ErrorMessage, SelectorHeader, SelectorLayout, Spoiler } from '~atoms';
import { useSelectorCollapse } from '~hooks';
import { ProductAdditionalInformation } from '~molecules';
import { Maybe, Product, ProductCategoryValidity } from '~types/appsync.types';

const VALIDITY_SKELETON_KEYS = Array.from({ length: 8 }, (_, i) => `validity-skeleton-${i}`);

interface ValidityChipsProps {
  displayMode: DisplayMode;
  sortedValidityCategories: ProductCategoryValidity[];
  validityCategorySelected?: ProductCategoryValidity;
  protectedByCodeByValidity: Record<string, boolean>;
  onValidityCategoryClick: (vc: ProductCategoryValidity) => void;
  intl: IntlShape;
}

const ValidityChips = ({
  displayMode,
  sortedValidityCategories,
  validityCategorySelected,
  protectedByCodeByValidity,
  onValidityCategoryClick,
  intl,
}: ValidityChipsProps) => {
  const chipGroup = (
    <ChipGroup
      onChange={(validityCategoryShortname) => {
        const currentValidityCategory = sortedValidityCategories.find(
          (vc) => vc.shortname === validityCategoryShortname,
        );
        if (!currentValidityCategory) return;
        onValidityCategoryClick(currentValidityCategory);
      }}
      value={validityCategorySelected?.shortname}
    >
      {sortedValidityCategories.map((currentValidityCategory: ProductCategoryValidity) => (
        <Chip
          data-testid='ValidityCategorySelector_validityCategory'
          key={currentValidityCategory.shortname}
          icon={
            validityCategorySelected?.shortname === currentValidityCategory.shortname
              ? undefined
              : protectedByCodeByValidity[currentValidityCategory.shortname]
                ? 'action-lock'
                : undefined
          }
          label={currentValidityCategory.name ?? currentValidityCategory.shortname}
          value={currentValidityCategory.shortname}
          aria-label={intl.formatMessage(
            {
              defaultMessage: `Choose a duration of ${currentValidityCategory.name} for this product`,
              id: 'chooseValidityCategory',
            },
            { validityCategoryName: currentValidityCategory.name },
          )}
        />
      ))}
    </ChipGroup>
  );

  if (displayMode === DISPLAY_MODE.COLLAPSE) {
    return <Spoiler maxHeight={75}>{chipGroup}</Spoiler>;
  }

  return chipGroup;
};

interface ValidityCategorySelectorProps {
  validityCategories?: Array<ProductCategoryValidity>;
  selectedProduct?: Maybe<Product>;
  selectedValidityCategory?: ProductCategoryValidity;
  isLoadingProductCategory?: boolean;
  products?: Array<Product>;
  onValidityCategoryClick: (validityCategorySelected: ProductCategoryValidity) => void;
  displayMode?: DisplayMode;
  disabled?: boolean;
  forceCollapsed?: boolean;
  onPopoverToggle?: () => void;
  onClickOutside?: () => void;
  hasProductCategoryError?: boolean;
}

const ValidityCategorySelector = ({
  validityCategories = [],
  selectedProduct,
  selectedValidityCategory,
  isLoadingProductCategory = false,
  products = [],
  onValidityCategoryClick,
  displayMode = DISPLAY_MODE.COLLAPSE,
  disabled = false,
  forceCollapsed,
  onPopoverToggle,
  onClickOutside,
  hasProductCategoryError = false,
}: ValidityCategorySelectorProps) => {
  const [validityCategorySelected, setValidityCategorySelected] = useState<
    ProductCategoryValidity | undefined
  >(selectedValidityCategory);
  const { isCollapsed, setIsCollapsed, toggle } = useSelectorCollapse({
    displayMode,
    forceCollapsed,
    initialCollapsed: displayMode === DISPLAY_MODE.POPOVER,
    onPopoverToggle,
  });

  const intl = useIntl();

  const handleValidityCategoryClick = useCallback(
    (newValidityCategorySelected: ProductCategoryValidity) => {
      setValidityCategorySelected(newValidityCategorySelected);
      onValidityCategoryClick(newValidityCategorySelected);
      setIsCollapsed(true);
    },
    [onValidityCategoryClick, setIsCollapsed],
  );

  useEffect(() => {
    setValidityCategorySelected(selectedValidityCategory);
    if (displayMode === DISPLAY_MODE.COLLAPSE) {
      setIsCollapsed(!!selectedValidityCategory);
    }
  }, [selectedValidityCategory, displayMode, setIsCollapsed]);

  const protectedByCodeByValidity = useMemo(() => {
    if (!products?.length) {
      return {} as Record<string, boolean>;
    }

    return products.reduce<Record<string, boolean>>((acc, product) => {
      const short = product.validityCategory?.shortname;
      if (short) {
        acc[short] = !!product.protectedByCode;
      }

      return acc;
    }, {});
  }, [products]);

  const hasProductForSelectedValidity = useMemo(() => {
    if (!validityCategorySelected || !products?.length) {
      return true;
    }
    return products.some(
      (product) => product.validityCategory?.shortname === validityCategorySelected.shortname,
    );
  }, [validityCategorySelected, products]);

  const hasAutoSelected = useRef(false);
  useEffect(() => {
    if (validityCategories?.length === 1 && !validityCategorySelected && !hasAutoSelected.current) {
      hasAutoSelected.current = true;
      handleValidityCategoryClick(validityCategories[0]);
    }
  }, [validityCategories, validityCategorySelected, handleValidityCategoryClick]);

  const selectorHeader = (
    <SelectorHeader
      isClosed={isCollapsed}
      isClickable={
        displayMode === DISPLAY_MODE.COLLAPSE && !!(validityCategorySelected && isCollapsed)
      }
      onToggle={() => setIsCollapsed(!isCollapsed)}
      onHeaderClick={() => setIsCollapsed(false)}
      showToggleButton={displayMode === DISPLAY_MODE.COLLAPSE && !!validityCategorySelected}
    >
      <div className='ValidityCategorySelector_title'>
        <div
          className={`ValidityCategorySelector_duration${disabled ? ' ValidityCategorySelector_duration-disabled' : ''}`}
        >
          <FormattedMessage id='duration' defaultMessage='Duration' />
        </div>

        {validityCategorySelected?.name &&
          (displayMode === DISPLAY_MODE.POPOVER || isCollapsed) && (
            <div className='ValidityCategorySelector_value'>{validityCategorySelected.name}</div>
          )}
      </div>
    </SelectorHeader>
  );

  return (
    <div className='ValidityCategorySelector'>
      <SelectorLayout
        displayMode={displayMode}
        isOpen={disabled ? false : !isCollapsed}
        onToggle={toggle}
        onClickOutside={onClickOutside}
        header={selectorHeader}
        popoverWidth='fit-content'
      >
        {disabled ? null : (
          <div className='ValidityCategorySelector_content'>
            {hasProductCategoryError && !isLoadingProductCategory && (
              <ErrorMessage
                messageId='noProductForSelectedDuration'
                defaultMessage='Unable to load information. Please try again.'
              />
            )}

            {!hasProductCategoryError &&
              validityCategories.length === 0 &&
              !isLoadingProductCategory && (
                <ErrorMessage
                  messageId='noDurationAvailable'
                  defaultMessage='No duration available at the moment.'
                />
              )}

            {!hasProductCategoryError &&
              validityCategorySelected &&
              !hasProductForSelectedValidity &&
              !isLoadingProductCategory && (
                <ErrorMessage
                  messageId='noProductForSelectedDuration'
                  defaultMessage='Unable to load information. Please try again.'
                />
              )}
            <div
              className='ValidityCategorySelector_validities'
              data-testid='ValidityCategorySelector_validities'
            >
              {isLoadingProductCategory ? (
                <div className={`ValidityCategorySelector_validities-placeholder`}>
                  {VALIDITY_SKELETON_KEYS.map((key) => (
                    <Skeleton
                      data-testid='Skeleton'
                      key={key}
                      height='32px'
                      width='70px'
                      radius='40px'
                    />
                  ))}
                </div>
              ) : (
                <ValidityChips
                  displayMode={displayMode}
                  sortedValidityCategories={validityCategories}
                  validityCategorySelected={validityCategorySelected}
                  protectedByCodeByValidity={protectedByCodeByValidity}
                  onValidityCategoryClick={handleValidityCategoryClick}
                  intl={intl}
                />
              )}
            </div>
          </div>
        )}
      </SelectorLayout>

      {displayMode === DISPLAY_MODE.COLLAPSE && (
        <ProductAdditionalInformation
          shortDescription={selectedProduct?.additionalInfo?.shortDescription}
          includedOptions={selectedProduct?.includedOptions}
        />
      )}
    </div>
  );
};

export default ValidityCategorySelector;
